import React, { useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Sparkles, TrendingUp, DollarSign, Users, Mail, Percent, ShoppingCart } from "lucide-react";

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold flex items-center gap-1.5 mb-2">
      <Icon className="size-3" strokeWidth={1.5} /> {label}
    </label>
    {children}
  </div>
);

export default function SuccessScore() {
  const [form, setForm] = useState({ prize_value: 500, ad_budget: 1000, follower_count: 5000, email_list_size: 2500, gross_margin: 40, avg_order_value: 45, purchase_rate: 30 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) }));

  const calculate = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/success-score", form);
      setResult(data);
    } finally { setLoading(false); }
  };

  const input = "w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none";

  const gradeColor = result?.grade === "A" ? "text-emerald-600" : result?.grade === "B" ? "text-zinc-950" : result?.grade === "C" ? "text-amber-600" : "text-red-600";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-8 py-10">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-emerald-600" strokeWidth={1.5} />
          <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-600 font-semibold">Killer Feature</span>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">Giveaway Success Score</h1>
        <p className="text-zinc-600 mt-2 max-w-2xl">Forecast your campaign's performance before you launch. Industry-benchmarked predictions from real sweepstakes data.</p>

        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          {/* Input panel */}
          <form onSubmit={calculate} className="bg-white border border-zinc-200 rounded-md p-6 space-y-5">
            <Field label="Prize value ($)" icon={DollarSign}>
              <input data-testid="score-prize-input" type="number" min={1} className={input} value={form.prize_value} onChange={handle("prize_value")} />
            </Field>
            <Field label="Ad budget ($)" icon={TrendingUp}>
              <input data-testid="score-budget-input" type="number" min={0} className={input} value={form.ad_budget} onChange={handle("ad_budget")} />
            </Field>
            <Field label="Follower count" icon={Users}>
              <input data-testid="score-followers-input" type="number" min={0} className={input} value={form.follower_count} onChange={handle("follower_count")} />
            </Field>
            <Field label="Email list size" icon={Mail}>
              <input data-testid="score-email-input" type="number" min={0} className={input} value={form.email_list_size} onChange={handle("email_list_size")} />
            </Field>

            <div className="pt-2 border-t border-zinc-100">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-3">Your store economics</div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Gross margin (%)" icon={Percent}>
                  <input data-testid="score-margin-input" type="number" min={0} max={100} className={input} value={form.gross_margin} onChange={handle("gross_margin")} />
                </Field>
                <Field label="Avg order ($)" icon={DollarSign}>
                  <input data-testid="score-aov-input" type="number" min={0} className={input} value={form.avg_order_value} onChange={handle("avg_order_value")} />
                </Field>
                <Field label="Purchase rate (%)" icon={ShoppingCart}>
                  <input data-testid="score-purchase-input" type="number" min={0} max={100} className={input} value={form.purchase_rate} onChange={handle("purchase_rate")} />
                </Field>
              </div>
            </div>
            <button
              type="submit"
              data-testid="calculate-score-button"
              disabled={loading}
              className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium text-sm hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {loading ? "Calculating…" : "Calculate Success Score"}
            </button>
          </form>

          {/* Result panel */}
          <div className={`relative ${result ? "success-glow" : ""} bg-white border border-zinc-200 rounded-md p-6`} data-testid="score-result">
            {!result ? (
              <div className="h-full flex items-center justify-center text-center py-16">
                <div>
                  <Sparkles className="size-10 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
                  <div className="text-zinc-500 text-sm">Enter your numbers to see predictions.</div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-baseline gap-3">
                  <div className="font-display text-6xl font-bold tabular">{result.score}</div>
                  <div className={`font-display text-3xl font-bold ${gradeColor}`}>{result.grade}</div>
                </div>
                <div className="text-sm text-zinc-600 mt-2">{result.advice}</div>

                <div className="grid grid-cols-3 gap-3 mt-6">
                  <div className="border border-zinc-100 rounded-md p-3 bg-zinc-50/50">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Entries</div>
                    <div className="font-display text-xl font-bold tabular mt-1" data-testid="result-entries">{result.expected_entries.toLocaleString()}</div>
                  </div>
                  <div className="border border-emerald-100 bg-emerald-50/40 rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-700 font-semibold">Revenue</div>
                    <div className="font-display text-xl font-bold tabular mt-1 text-emerald-700" data-testid="result-revenue">${result.expected_revenue.toLocaleString()}</div>
                  </div>
                  <div className="border border-zinc-100 rounded-md p-3 bg-zinc-50/50">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">ROAS</div>
                    <div className="font-display text-xl font-bold tabular mt-1" data-testid="result-roas">{result.roas_target ? `${result.roas_target}x` : "—"}</div>
                  </div>
                  <div className="border border-zinc-100 rounded-md p-3 bg-zinc-50/50">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Profit Est.</div>
                    <div className={`font-display text-xl font-bold tabular mt-1 ${result.profit_estimate >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="result-profit">${result.profit_estimate.toLocaleString()}</div>
                  </div>
                  <div className="border border-zinc-100 rounded-md p-3 bg-zinc-50/50">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Health</div>
                    <div className={`font-display text-xl font-bold mt-1 ${result.health_color === "emerald" ? "text-emerald-700" : result.health_color === "amber" ? "text-amber-600" : "text-red-600"}`} data-testid="result-health">{result.health_status}</div>
                  </div>
                  <div className="border border-zinc-100 rounded-md p-3 bg-zinc-50/50">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Multiplier</div>
                    <div className="font-display text-xl font-bold tabular mt-1">{result.health_score}x</div>
                  </div>
                </div>

                <div className="mt-3 text-xs text-zinc-500" data-testid="score-economics-note">
                  Based on your economics: <span className="font-medium text-zinc-700">{form.gross_margin}% margin</span> · <span className="font-medium text-zinc-700">${form.avg_order_value} avg order</span> · <span className="font-medium text-zinc-700">{form.purchase_rate}% purchase rate</span>
                </div>

                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-2">Entry Breakdown</div>
                  <div className="space-y-1.5">
                    {[
                      ["From paid ads", result.breakdown.from_ads],
                      ["From followers", result.breakdown.from_followers],
                      ["From email list", result.breakdown.from_email],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-xs">
                        <span className="text-zinc-600">{k}</span>
                        <span className="tabular font-medium">{v.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-zinc-100">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-2">Industry Benchmarks</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(result.benchmarks).map(([k, v]) => (
                      <div key={k} className="text-zinc-600">
                        <span className="capitalize">{k.replace(/_/g, " ")}: </span>
                        <span className="font-medium text-zinc-950">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
